import React, { useEffect, useMemo, useState } from 'react';
import {
  AddUniversal,
  FilterOutlined,
  MoreInfoOutlined,
  TrashCanOutlined,
  WarningAltOutlined,
} from '@duxton-web/icons';
import {
  Button,
  Checkbox,
  ConfigProvider,
  Divider,
  Flex,
  InputNumber,
  notification,
  Select,
  Tag,
  Tooltip,
  Typography,
} from '@duxton-web/ui';
import { produce } from 'immer';
import { useParams } from 'react-router-dom';
import { usePrevious } from 'react-use';
import styled from 'styled-components';

import {
  Dimension,
  DraftDimensionFilterConstraint,
  getRestrictByConstraint,
  isTimeSliceStrategy,
  isViewMode,
  MetricState,
  MetricType,
  MetricWithDimensionFields,
} from '../../constants';
import {
  useExperimentContext,
  useExperimentDispatchContext,
} from '../../contexts/experiment-context';
import { updateAnalysisConfig } from '../../store/actions/experiment/action-creators';
import {
  constructDimensionFields,
  getDimensionsFromAnalysisConfig,
} from '../../utils';
import { MetricFilter } from './metric-filter';
import { isFilterNonEditable } from './population-filter-utils';
import Service, { Operators } from '@/lib/App';
import FullDivider from '@/pages/ExperimentFormV2/common/FullDivider';

const { Text } = Typography;

const JoinStrategyType = {
  TRANSACTING: 'TRANSACTING',
  EXPOSED: 'EXPOSED',
};
export type JoinStrategy =
  (typeof JoinStrategyType)[keyof typeof JoinStrategyType];

const MetricTypeToFieldMap: Record<MetricType, string> = {
  primary: 'primary_metrics',
  secondary: 'secondary_metrics',
  guardrail: 'guardrail_metrics',
  control: 'control_metrics',
};

type Props = {
  prefix?: string;
  filterEnabled: boolean;
  strategy: string;
  metricsGlossaryData: any[];
  metricType: MetricType;
  metricIndex: number;
  onSearch: (value: string) => void;
  options: React.ReactNode[];
  metrics: MetricState;
  onDelete: () => void;
  showValidation: boolean;
  // Optional callback for custom metric selection (used by control metrics with duplicate names)
  onMetricSelect?: (selectedValue: string, glossaryItem: any) => void;
  // Optional unique key field name for finding metrics (default: 'name')
  uniqueKeyField?: string;
  // Optional value to display in the selector (for metrics with candidate_id)
  displayValue?: string;
};

const MetricRow = ({
  prefix = '',
  filterEnabled,
  strategy,
  metricsGlossaryData,
  metricType,
  metricIndex,
  onSearch,
  options,
  metrics,
  onDelete,
  showValidation,
  onMetricSelect,
  uniqueKeyField = 'name',
  displayValue,
}: Props) => {
  const params = useParams<{ action: string }>();
  const { action } = params;
  const readOnly = isViewMode(action);

  const experimentContext = useExperimentContext();
  const experimentDispatch = useExperimentDispatchContext();
  const { analysis, experiment } = experimentContext;
  const config = analysis?.analysisConfig;

  const [showFilter, setShowFilter] = useState(false);

  const metric: MetricWithDimensionFields | null = useMemo(() => {
    if (!metrics) {
      return null;
    }

    return metrics[MetricTypeToFieldMap[metricType]]?.[metricIndex] || null;
  }, [metricType, metricIndex, metrics]);
  const previousMetric = usePrevious(metric);

  const [dimensions, setDimensions] = useState<Dimension[] | null>(null);

  const [fetchingDimensions, setFetchingDimensions] = useState(false);

  const fetchDimensions = (metricName: string) => {
    setFetchingDimensions(true);

    if (readOnly && config) {
      const dimensions = getDimensionsFromAnalysisConfig(config, metricName);
      setDimensions(dimensions);
      setFetchingDimensions(false);
      return;
    }

    // Determine if exposed join is enabled for this metric
    const glossaryMetric = metricsGlossaryData.find(
      (m) => m.name === metricName,
    );
    const isExposedJoinEnabledForMetric =
      glossaryMetric?.is_exposed_join_enabled ??
      metric?.is_exposed_join_enabled ??
      false;

    // For create/edit mode, use MIDAS API
    const fetchPromise = Service.planner
      .fetchDimensions(metricName, {
        purpose: 'filters',
        strategy,
        joinStrategy: isExposedJoinEnabledForMetric
          ? metric?.join_strategy
          : null,
      })
      .then((d) => d.dimensions || []);

    fetchPromise
      .then((dimensions) => {
        setDimensions(dimensions);
      })
      .catch((e) => {
        setDimensions([]);
        notification.error({
          message: 'Error fetching dimensions',
          description: e?.message || 'Unknown error',
        });
      })
      .finally(() => {
        setFetchingDimensions(false);
      });
  };

  useEffect(() => {
    if (
      metric?.name &&
      (previousMetric?.name !== metric.name ||
        previousMetric?.join_strategy !== metric.join_strategy)
    ) {
      fetchDimensions(metric?.name);
    }
  }, [metric?.name, metric?.join_strategy]);

  const noDimensionsForMetric = useMemo(() => {
    return dimensions && dimensions.length === 0;
  }, [dimensions]);

  const dimensionFilterConstraints = useMemo(() => {
    return metric?.dimension_filters?.[0]?.dimension_filter_constraints || [];
  }, [metric]);

  const previousDimensionFilterConstraints = usePrevious(
    dimensionFilterConstraints,
  );

  useEffect(() => {
    // Show existing filters or join settings when metric first initialized
    if (
      !previousMetric &&
      metric &&
      (dimensionFilterConstraints.length || metric.join_strategy)
    ) {
      setShowFilter(true);
    }

    // Also show filters when they are auto-populated (constraints added after metric exists)
    // Only auto-show if experiment has population constraints (city/country/regional)
    // to avoid rendering MetricFilter when it can't properly fetch dimension values
    const hasPopulationConstraints = !!getRestrictByConstraint(experiment);
    if (
      previousMetric &&
      metric &&
      !previousDimensionFilterConstraints?.length &&
      dimensionFilterConstraints.length > 0 &&
      hasPopulationConstraints
    ) {
      setShowFilter(true);
    }
  }, [
    metric,
    dimensionFilterConstraints,
    previousMetric,
    previousDimensionFilterConstraints,
    experiment,
  ]);

  const metricFromGlossary = useMemo(() => {
    if (!metric?.name) return null;

    // Try to find in glossary first (always prefer fresh glossary data)
    const foundMetric = metricsGlossaryData.find((m) => m.name === metric.name);
    if (foundMetric) return foundMetric;

    // Fallback to metric itself for cloned/imported metrics not in glossary
    // Only use metric as fallback if it has join settings defined
    if (metric.is_exposed_join_enabled !== undefined) return metric;

    return null;
  }, [metric, metricsGlossaryData]);

  const isExposedJoinEnabled =
    metricFromGlossary?.is_exposed_join_enabled ??
    metric?.is_exposed_join_enabled ??
    false;

  const isRatioMetric = (metricFromGlossary?.type ?? metric?.type) === 'ratio';

  const handleLogTransformationChange = (checked: boolean) => {
    const nextAnalysisConfig = produce(config, (draft) => {
      const field = MetricTypeToFieldMap[metricType];
      draft[field][metricIndex].is_log_transformation_enabled = checked;
    });
    experimentDispatch(updateAnalysisConfig(nextAnalysisConfig));
  };

  const handleJoinStrategyChange = (value: JoinStrategy) => {
    const nextAnalysisConfig = produce(config, (draft) => {
      const field = MetricTypeToFieldMap[metricType];
      draft[field][metricIndex].join_strategy = value;
      if (value === JoinStrategyType.TRANSACTING) {
        delete draft[field][metricIndex].metric_default_value;
      } else if (value === JoinStrategyType.EXPOSED) {
        draft[field][metricIndex].metric_default_value =
          draft[field][metricIndex].metric_default_value ?? 0;
      }
    });
    experimentDispatch(updateAnalysisConfig(nextAnalysisConfig));
  };

  const handleDefaultValueChange = (value: number | null) => {
    const nextAnalysisConfig = produce(config, (draft) => {
      const field = MetricTypeToFieldMap[metricType];
      draft[field][metricIndex].metric_default_value =
        value !== null && value !== undefined ? value : 0;
    });
    experimentDispatch(updateAnalysisConfig(nextAnalysisConfig));
  };

  const handleDeleteFilter = (index: number) => {
    const nextAnalysisConfig = produce(config, (draft) => {
      const field = MetricTypeToFieldMap[metricType];
      draft[field][
        metricIndex
      ].dimension_filters[0].dimension_filter_constraints.splice(index, 1);
    });
    experimentDispatch(updateAnalysisConfig(nextAnalysisConfig));
  };

  const handleShowFilterChange = () => {
    if (!showFilter) {
      const nextAnalysisConfig = produce(config, (draft) => {
        const field = MetricTypeToFieldMap[metricType];
        draft[field][metricIndex].join_strategy = JoinStrategyType.EXPOSED;
        draft[field][metricIndex].metric_default_value =
          metric?.metric_default_value || 0;
      });
      experimentDispatch(updateAnalysisConfig(nextAnalysisConfig));
    } else {
      const nextAnalysisConfig = produce(config, (draft) => {
        const field = MetricTypeToFieldMap[metricType];
        delete draft[field][metricIndex].join_strategy;
        delete draft[field][metricIndex].metric_default_value;
      });
      experimentDispatch(updateAnalysisConfig(nextAnalysisConfig));
    }

    setShowFilter((prev) => !prev);
  };

  const handleUpdateFilter = (
    index: number,
    constraint: DraftDimensionFilterConstraint,
  ) => {
    const nextAnalysisConfig = produce(config, (draft) => {
      const field = MetricTypeToFieldMap[metricType];
      draft[field][
        metricIndex
      ].dimension_filters[0].dimension_filter_constraints[index] = constraint;
    });
    experimentDispatch(updateAnalysisConfig(nextAnalysisConfig));
  };

  const handleAddFilter = () => {
    const nextAnalysisConfig = produce(config, (draft) => {
      const field = MetricTypeToFieldMap[metricType];
      draft[field][
        metricIndex
      ].dimension_filters[0].dimension_filter_constraints.push({
        dimension_name: null,
        operator: Operators.in,
        values: [],
      });
    });

    experimentDispatch(updateAnalysisConfig(nextAnalysisConfig));
  };

  if (!metric) {
    return null;
  }

  const renderFilter = () => {
    // In read-only mode, show existing filters even without dimensions list
    // (needed for control metrics with pre-defined filters)
    if (readOnly && !dimensionFilterConstraints.length) {
      return null;
    }

    // In edit mode, need dimensions to render filter UI
    if (!readOnly && !dimensions?.length) {
      return null;
    }

    if (!readOnly && !showFilter) {
      return null;
    }

    return (
      <FilterWrapper>
        <FullDivider
          orientation="left"
          orientationMargin={0}
          style={{ marginTop: 8, marginBottom: 8 }}
        >
          <Tag color="#3D3D3D" style={{ borderRadius: 8 }}>
            Filters
          </Tag>
        </FullDivider>
        <Flex vertical gap={8}>
          {dimensionFilterConstraints.map((constraint, index) => (
            <MetricFilter
              metricName={metric.name!}
              metricVersion={metric.version}
              key={`filter-constraint-${index}-${constraint.dimension_name}`}
              constraint={constraint}
              dimensions={dimensions?.filter(
                (d) =>
                  d.name === constraint.dimension_name ||
                  !dimensionFilterConstraints.some(
                    (filter, i) =>
                      i !== index && filter.dimension_name === d.name,
                  ),
              )}
              onDelete={() => handleDeleteFilter(index)}
              onUpdate={(constraint) => handleUpdateFilter(index, constraint)}
              showValidation={showValidation}
              isNonEditable={isFilterNonEditable(
                constraint.dimension_name,
                metric.populationDerivedFilters,
              )}
            />
          ))}
          {!readOnly &&
            dimensionFilterConstraints.length < dimensions?.length && (
              <Flex gap={8}>
                <AndTag>AND</AndTag>
                <ConfigProvider
                  theme={{
                    token: {
                      fontWeightStrong: 400,
                    },
                    components: {
                      Button: {
                        contentFontSize: 14,
                        defaultBorderColor: '#bfbfbf',
                      },
                    },
                  }}
                >
                  <Button
                    icon={<AddUniversal />}
                    onClick={handleAddFilter}
                    shape="default"
                  >
                    Add Filter
                  </Button>
                </ConfigProvider>
              </Flex>
            )}
        </Flex>
      </FilterWrapper>
    );
  };

  const renderFilterButton = () => {
    return noDimensionsForMetric ? (
      <Tooltip title="There are no filter dimensions for this metric">
        <Button
          size="small"
          shape="circle"
          icon={<FilterOutlined />}
          disabled
        />
      </Tooltip>
    ) : (
      <Button
        size="small"
        shape="circle"
        onClick={() => {
          handleShowFilterChange();
        }}
        icon={<FilterOutlined />}
        disabled={!metric?.name || fetchingDimensions}
      />
    );
  };

  const getMetricTagText = (index: number) => {
    if (metricType === 'primary') {
      return `P0`;
    }

    if (!prefix) {
      return index;
    }

    return `${prefix}-${index}`;
  };

  const renderJoinSettings = () => {
    const shouldShow =
      metric?.name &&
      (readOnly
        ? metric.is_exposed_join_enabled || !!metric.join_strategy
        : isExposedJoinEnabled) &&
      showFilter;

    if (!shouldShow) {
      return null;
    }

    return (
      <JoinSettingsWrapper>
        <Text strong style={{ marginTop: 8, marginBottom: 8 }}>
          Join Settings
        </Text>
        <Flex vertical gap={8}>
          <Flex vertical gap={4}>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 4,
              }}
            >
              <Text>Type:</Text>
              <Tooltip
                color="#FFF"
                title={
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 4,
                      backgroundColor: '#FFF',
                    }}
                  >
                    <Typography.Text>
                      <Typography.Text strong>Exposed</Typography.Text>:
                      Includes all exposed users in the experiment
                    </Typography.Text>
                    <Typography.Text>
                      <Typography.Text strong>Transacting</Typography.Text>:
                      Includes only transacting users present in metric table
                    </Typography.Text>
                    <Typography.Text>
                      to know more -{' '}
                      <a
                        href="https://docs.google.com/document/d/1P-ESVZqPZhSJa47j8MxFiBq59_zK34qdwB12rKyRrS8"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Release document
                      </a>
                    </Typography.Text>
                  </div>
                }
              >
                <MoreInfoOutlined />
              </Tooltip>
            </div>
            <Select
              style={{ width: 200 }}
              value={
                metric.join_strategy ||
                (isExposedJoinEnabled
                  ? JoinStrategyType.EXPOSED
                  : JoinStrategyType.TRANSACTING)
              }
              onChange={handleJoinStrategyChange}
              disabled={readOnly}
              options={[
                { label: 'Transacting', value: JoinStrategyType.TRANSACTING },
                { label: 'Exposed', value: JoinStrategyType.EXPOSED },
              ]}
            />
          </Flex>
          {(metric.join_strategy === JoinStrategyType.EXPOSED ||
            (!metric.join_strategy && isExposedJoinEnabled)) && (
            <Flex vertical gap={4}>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 4,
                }}
              >
                <Text>Default Value:</Text>
                <Tooltip
                  color="#FFF"
                  title={
                    <Typography.Text>
                      In case of exposed user strategy, we populate this value
                      for null metrics
                    </Typography.Text>
                  }
                >
                  <MoreInfoOutlined />
                </Tooltip>
              </div>
              <InputNumber
                style={{ width: 200 }}
                precision={2}
                value={
                  metric.metric_default_value !== undefined
                    ? Number(metric.metric_default_value)
                    : 0
                }
                onChange={handleDefaultValueChange}
                placeholder="0"
                disabled={readOnly}
              />
            </Flex>
          )}
        </Flex>
      </JoinSettingsWrapper>
    );
  };

  const renderLogTransformation = () => {
    if (!metric?.name || !isTimeSliceStrategy(strategy)) return null;

    return (
      <div style={{ marginTop: 8 }}>
        <Checkbox
          checked={metric.is_log_transformation_enabled ?? false}
          disabled={readOnly}
          onChange={(e) => handleLogTransformationChange(e.target.checked)}
        >
          <span
            style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}
          >
            Apply log transformation
            <Tooltip
              title={
                <span>
                  If opted-in, AXA will apply log transformation on this metric
                  and the scorecard will reflect the same. For more information
                  on how log transformation will be applied, refer{' '}
                  <a
                    href="https://techdocs.grab.com/product-docs/grabx/experiment-decision-engine/statstools/ts_latest/#log-transforming-your-metric"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    here
                  </a>
                  .
                </span>
              }
            >
              <MoreInfoOutlined />
            </Tooltip>
            {isRatioMetric && (
              <Tooltip title="Log-transformation is not recommended for binary ratio metrics.">
                <WarningAltOutlined style={{ color: '#faad14' }} />
              </Tooltip>
            )}
          </span>
        </Checkbox>
      </div>
    );
  };

  return (
    <MetricRowUI>
      <MetricTagUI>{getMetricTagText(metricIndex + 1)}</MetricTagUI>
      <Container>
        <Flex align="center" gap={8}>
          <Select
            showSearch
            showAction={['focus']}
            style={{ width: '100%' }}
            placeholder="Select Metric"
            dropdownRender={(menu) => {
              return (
                <>
                  {menu}
                  <Divider style={{ margin: '4px 0' }} />
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      padding: '0 8px',
                    }}
                  >
                    <div
                      style={{ display: 'flex', gap: 4, alignItems: 'center' }}
                    >
                      <MoreInfoOutlined />
                      Analysis Unit (AU) is considered by GrabX to compute
                      scorecard for the metrics associated with the experiments
                    </div>
                    <Text strong style={{ paddingLeft: 10 }}>
                      Showing {metricsGlossaryData.length} results
                    </Text>
                  </div>
                </>
              );
            }}
            optionFilterProp="metadata.display_name"
            optionLabelProp="label"
            filterOption={false}
            onSearch={onSearch}
            onChange={(selectedValue) => {
              // Find the metric using the unique key field (name or candidate_id)
              const glossaryItem = metricsGlossaryData.find(
                (m) => m[uniqueKeyField] === selectedValue,
              );

              // If custom callback is provided, use it (for control metrics with duplicates)
              if (onMetricSelect) {
                onMetricSelect(selectedValue, glossaryItem);
                setShowFilter(false);
                onSearch('');
                return;
              }

              // Default behavior: dispatch to experiment context
              const nextAnalysisConfig = produce(config, (draft) => {
                const field = MetricTypeToFieldMap[metricType];
                draft[field][metricIndex] =
                  constructDimensionFields(glossaryItem);

                if (!showFilter) {
                  delete draft[field][metricIndex].join_strategy;
                  delete draft[field][metricIndex].metric_default_value;
                }
              });
              experimentDispatch(updateAnalysisConfig(nextAnalysisConfig));
              setShowFilter(false);
              onSearch('');
            }}
            suffixIcon={
              !!metric?.analysis_dimension && (
                <Tag
                  color="green"
                  size="xsmall"
                  style={{ height: 'fit-content' }}
                >
                  {`AU = ${metric?.analysis_dimension}`}
                </Tag>
              )
            }
            value={displayValue ?? metric?.name}
            disabled={readOnly}
          >
            {options}
          </Select>
          {!readOnly && (
            <>
              <Button
                size="small"
                shape="circle"
                icon={<TrashCanOutlined />}
                disabled={!metric}
                onClick={onDelete}
              />
              {filterEnabled && renderFilterButton()}
            </>
          )}
        </Flex>
        {renderFilter()}
        {renderJoinSettings()}
        {renderLogTransformation()}
      </Container>
    </MetricRowUI>
  );
};

export default MetricRow;

export const MetricTagUI = styled(Tag)<{ hasMetricID?: boolean }>`
  border-radius: 8px;
  border: none;
  background-color: #f5f5f5;
  color: #3d3d3d;
  font-size: 12px;
  font-weight: 700;
  display: flex;
  min-height: 40px;
  min-width: 40px;
  width: ${({ hasMetricID }) => (hasMetricID ? '227px' : '40px')};
  align-items: center;
  justify-content: center;
  margin: 0;
`;

const MetricRowUI = styled.div`
  display: flex;
  gap: 8px;
  padding: 4px 0;
  align-items: stretch;
  min-width: 80%;
`;

const Container = styled.div`
  flex-grow: 1;
`;

const FilterWrapper = styled.div`
  display: flex;
  flex-direction: column;
`;

const JoinSettingsWrapper = styled.div`
  display: flex;
  flex-direction: column;
`;

export const AndTag = styled(Tag)`
  border-radius: 8px;
  background-color: #f5f5f5;
  color: #3d3d3d;
  font-size: 12px;
  font-weight: 700;
  display: flex;
  min-height: 40px;
  align-items: center;
  justify-content: center;
  margin-inline-end: 0px;
`;
